import React, { useState } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faLock, faEye, faEyeSlash, faCheckCircle, faTimesCircle, faArrowLeft } from '@fortawesome/free-solid-svg-icons';
import { FaTiktok, FaFacebookF, FaLinkedinIn, FaGoogle } from 'react-icons/fa';
import logo from 'assets/images/logo.png';
import { postRequest } from '../../services/api/apiService';

const BG_PILLS = [
  { icon: <FaTiktok />, label: 'TikTok Ads', color: '#010101', bg: '#F3F4F6', border: '#D1D5DB', top: '8%', left: '3%', rotate: '-6deg' },
  { icon: <FaFacebookF />, label: 'Meta Ads', color: '#1877f2', bg: '#EEF4FF', border: '#BFDBFE', top: '18%', right: '4%', rotate: '5deg' },
  { icon: <FaLinkedinIn />, label: 'LinkedIn Ads', color: '#0a66c2', bg: '#EFF6FF', border: '#BAE6FD', bottom: '22%', left: '2%', rotate: '4deg' },
  { icon: <FaGoogle />, label: 'Google Ads', color: '#4285f4', bg: '#EFF6FF', border: '#BFDBFE', bottom: '12%', right: '3%', rotate: '-4deg' },
  { icon: '👻', label: 'Snapchat', color: '#8B7500', bg: '#FEFCE8', border: '#FDE68A', top: '55%', left: '1%', rotate: '8deg' },
  { icon: '🛒', label: 'Shopify', color: '#5a7a1a', bg: '#F0FDF4', border: '#BBF7D0', top: '42%', right: '2%', rotate: '-7deg' },
];

const ResetPassword = () => {
  const [searchParams]        = useSearchParams();
  const token                  = searchParams.get('token') || '';
  const navigate               = useNavigate();

  const [password, setPassword] = useState('');
  const [confirm, setConfirm]   = useState('');
  const [showPw, setShowPw]     = useState(false);
  const [loading, setLoading]   = useState(false);
  const [success, setSuccess]   = useState(false);
  const [error, setError]       = useState('');

  const strength = password.length === 0 ? 0 : password.length < 6 ? 1 : password.length < 10 ? 2 : 3;
  const strengthLabel = ['', 'Weak', 'Good', 'Strong'];
  const strengthColor = ['', '#EF4444', '#F59E0B', '#059669'];

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!password) { setError('Please enter a new password.'); return; }
    if (password !== confirm) { setError('Passwords do not match.'); return; }
    if (!token) { setError('Invalid reset link. Please request a new one.'); return; }
    setError(''); setLoading(true);
    try {
      await postRequest('/api/auth/reset-password', { token, password });
      setSuccess(true);
      setTimeout(() => navigate('/login'), 3000);
    } catch (err) {
      setError(err.message || 'Failed to reset password. The link may have expired.');
    } finally {
      setLoading(false);
    }
  };

  if (!token) {
    return (
      <div style={{ minHeight: '100vh', background: '#F6F7FB', display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 24 }}>
        <div style={{ background: '#fff', border: '1px solid #FECACA', borderRadius: 20, padding: '40px 36px', maxWidth: 420, textAlign: 'center', boxShadow: '0 8px 40px rgba(34,28,81,0.1)' }}>
          <div style={{ width: 52, height: 52, borderRadius: '50%', background: '#FEF2F2', border: '1.5px solid #FECACA', display: 'flex', alignItems: 'center', justifyContent: 'center', margin: '0 auto 16px' }}>
            <FontAwesomeIcon icon={faTimesCircle} style={{ fontSize: 22, color: '#EF4444' }} />
          </div>
          <h2 style={{ color: '#0F0B2D', fontSize: 20, fontWeight: 800, marginBottom: 10 }}>Invalid reset link</h2>
          <p style={{ color: '#6B7280', fontSize: 14, marginBottom: 24, lineHeight: 1.6 }}>This password reset link is missing a token. Please request a new one.</p>
          <Link to="/forgot-password" style={{ display: 'inline-block', padding: '12px 28px', borderRadius: 10, background: '#221C51', color: '#fff', textDecoration: 'none', fontWeight: 700, fontSize: 14 }}>Request new link</Link>
        </div>
      </div>
    );
  }

  return (
    <div style={{ minHeight: '100vh', background: '#F6F7FB', display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', padding: '32px 16px', position: 'relative', overflow: 'hidden' }}>
      <style>{`
        @keyframes authFloat { 0%,100%{transform:translateY(0) rotate(var(--r,0deg))} 50%{transform:translateY(-8px) rotate(var(--r,0deg))} }
        .rp-input {
          width: 100%; padding: 12px 40px 12px 42px;
          border: 1.5px solid #E5E7EB; border-radius: 10px;
          font-size: 14px; color: #0F0B2D; background: #F9FAFB;
          outline: none; box-sizing: border-box;
          transition: border 0.2s, box-shadow 0.2s, background 0.2s;
        }
        .rp-input::placeholder { color: #9CA3AF; }
        .rp-input:focus { border-color: #221C51; background: #fff; box-shadow: 0 0 0 3px rgba(34,28,81,0.09); }
        .rp-input.error { border-color: #EF4444; background: #fff; }
        .rp-input-confirm {
          width: 100%; padding: 12px 14px 12px 42px;
          border: 1.5px solid #E5E7EB; border-radius: 10px;
          font-size: 14px; color: #0F0B2D; background: #F9FAFB;
          outline: none; box-sizing: border-box;
          transition: border 0.2s, box-shadow 0.2s, background 0.2s;
        }
        .rp-input-confirm::placeholder { color: #9CA3AF; }
        .rp-input-confirm:focus { border-color: #221C51; background: #fff; box-shadow: 0 0 0 3px rgba(34,28,81,0.09); }
        .rp-btn {
          width: 100%; padding: 13px; border-radius: 10px;
          background: #221C51; color: #fff; font-size: 15px; font-weight: 700;
          border: none; cursor: pointer; transition: all 0.2s ease;
        }
        .rp-btn:hover:not(:disabled) { background: #2D2570; transform: translateY(-1px); box-shadow: 0 8px 24px rgba(34,28,81,0.25); }
        .rp-btn:disabled { opacity: 0.6; cursor: not-allowed; }
      `}</style>

      {/* Floating platform pills */}
      {BG_PILLS.map((p, i) => (
        <div key={i} style={{
          position: 'absolute',
          top: p.top, bottom: p.bottom, left: p.left, right: p.right,
          display: 'flex', alignItems: 'center', gap: 7,
          padding: '8px 14px', borderRadius: 10,
          background: p.bg, border: `1px solid ${p.border}`,
          fontSize: 12, fontWeight: 600, color: p.color,
          boxShadow: '0 2px 8px rgba(0,0,0,0.06)',
          opacity: 0.85,
          transform: `rotate(${p.rotate})`,
          animation: `authFloat ${5 + i * 0.8}s ease-in-out infinite`,
          pointerEvents: 'none',
          whiteSpace: 'nowrap',
        }}>
          <span style={{ fontSize: 14 }}>{p.icon}</span>
          {p.label}
        </div>
      ))}

      {/* Subtle blob background */}
      <div style={{ position: 'absolute', top: '-120px', left: '-120px', width: 500, height: 500, borderRadius: '50%', background: 'radial-gradient(circle, rgba(34,28,81,0.07) 0%, transparent 60%)', pointerEvents: 'none' }} />
      <div style={{ position: 'absolute', bottom: '-100px', right: '-100px', width: 400, height: 400, borderRadius: '50%', background: 'radial-gradient(circle, rgba(79,70,229,0.06) 0%, transparent 60%)', pointerEvents: 'none' }} />

      {/* Logo */}
      <Link to="/" style={{ marginBottom: 28, display: 'inline-block', position: 'relative', zIndex: 1 }}>
        <img src={logo} alt="OneBotAds" style={{ height: 34 }} />
      </Link>

      {/* Card */}
      <div style={{
        width: '100%', maxWidth: 420,
        background: '#fff', borderRadius: 20,
        border: '1px solid rgba(34,28,81,0.1)',
        padding: '36px 36px 32px',
        boxShadow: '0 8px 40px rgba(34,28,81,0.1), 0 2px 8px rgba(0,0,0,0.05)',
        position: 'relative', zIndex: 1,
      }}>
        <Link to="/login" style={{ display: 'inline-flex', alignItems: 'center', gap: 8, color: '#6B7280', fontSize: 13, textDecoration: 'none', marginBottom: 24, fontWeight: 500, transition: 'color 0.15s' }}
          onMouseEnter={e => e.currentTarget.style.color = '#221C51'}
          onMouseLeave={e => e.currentTarget.style.color = '#6B7280'}
        >
          <FontAwesomeIcon icon={faArrowLeft} style={{ fontSize: 11 }} /> Back to login
        </Link>

        <div style={{ width: 48, height: 48, borderRadius: 14, background: '#EEF2FF', border: '1.5px solid #C7D2FE', display: 'flex', alignItems: 'center', justifyContent: 'center', marginBottom: 18 }}>
          <FontAwesomeIcon icon={faLock} style={{ fontSize: 18, color: '#221C51' }} />
        </div>

        {success ? (
          <div style={{ textAlign: 'center', paddingTop: 8 }}>
            <div style={{ width: 56, height: 56, borderRadius: '50%', background: '#ECFDF5', border: '1.5px solid #A7F3D0', display: 'flex', alignItems: 'center', justifyContent: 'center', margin: '0 auto 18px' }}>
              <FontAwesomeIcon icon={faCheckCircle} style={{ color: '#059669', fontSize: 24 }} />
            </div>
            <h2 style={{ fontSize: 22, fontWeight: 800, color: '#0F0B2D', marginBottom: 10, letterSpacing: '-0.02em' }}>Password updated!</h2>
            <p style={{ fontSize: 14, color: '#6B7280', marginBottom: 24, lineHeight: 1.6 }}>
              Your password has been changed successfully. Redirecting you to login…
            </p>
            <Link to="/login" style={{ display: 'inline-block', padding: '13px 32px', borderRadius: 10, background: '#221C51', color: '#fff', fontWeight: 700, fontSize: 14, textDecoration: 'none' }}>
              Go to Login
            </Link>
          </div>
        ) : (
          <>
            <h1 style={{ fontSize: 22, fontWeight: 800, color: '#0F0B2D', marginBottom: 8, letterSpacing: '-0.02em' }}>Set new password</h1>
            <p style={{ fontSize: 14, color: '#6B7280', marginBottom: 24, lineHeight: 1.6 }}>
              Choose a strong password for your account.
            </p>

            <form onSubmit={handleSubmit}>
              {/* New password */}
              <label style={{ display: 'block', fontSize: 13, fontWeight: 600, color: '#374151', marginBottom: 7 }}>New password</label>
              <div style={{ position: 'relative', marginBottom: 8 }}>
                <FontAwesomeIcon icon={faLock} style={{ position: 'absolute', left: 14, top: '50%', transform: 'translateY(-50%)', color: '#9CA3AF', fontSize: 13, pointerEvents: 'none' }} />
                <input
                  type={showPw ? 'text' : 'password'}
                  value={password}
                  onChange={e => { setPassword(e.target.value); setError(''); }}
                  placeholder="Enter new password"
                  className={`rp-input${error && !password ? ' error' : ''}`}
                />
                <button type="button" onClick={() => setShowPw(s => !s)} style={{ position: 'absolute', right: 14, top: '50%', transform: 'translateY(-50%)', background: 'none', border: 'none', color: '#9CA3AF', cursor: 'pointer', fontSize: 13, padding: 0 }}>
                  <FontAwesomeIcon icon={showPw ? faEyeSlash : faEye} />
                </button>
              </div>

              {/* Strength bar */}
              {password.length > 0 && (
                <div style={{ marginBottom: 16 }}>
                  <div style={{ display: 'flex', gap: 4, marginBottom: 4 }}>
                    {[1, 2, 3].map(i => (
                      <div key={i} style={{ flex: 1, height: 3, borderRadius: 2, background: i <= strength ? strengthColor[strength] : '#E5E7EB', transition: 'background 0.2s' }} />
                    ))}
                  </div>
                  <span style={{ fontSize: 11, color: strengthColor[strength], fontWeight: 600 }}>{strengthLabel[strength]}</span>
                </div>
              )}
              {password.length === 0 && <div style={{ marginBottom: 16 }} />}

              {/* Confirm password */}
              <label style={{ display: 'block', fontSize: 13, fontWeight: 600, color: '#374151', marginBottom: 7 }}>Confirm password</label>
              <div style={{ position: 'relative', marginBottom: 6 }}>
                <FontAwesomeIcon icon={faLock} style={{ position: 'absolute', left: 14, top: '50%', transform: 'translateY(-50%)', color: '#9CA3AF', fontSize: 13, pointerEvents: 'none' }} />
                <input
                  type={showPw ? 'text' : 'password'}
                  value={confirm}
                  onChange={e => { setConfirm(e.target.value); setError(''); }}
                  placeholder="Repeat new password"
                  className={`rp-input-confirm${confirm && confirm !== password ? ' error' : ''}`}
                  style={{ borderColor: confirm && confirm !== password ? '#EF4444' : undefined }}
                />
              </div>

              {error && <p style={{ fontSize: 12, color: '#EF4444', marginBottom: 14 }}>{error}</p>}
              {!error && <div style={{ marginBottom: 20 }} />}

              <button type="submit" disabled={loading} className="rp-btn">
                {loading ? 'Updating password…' : 'Reset Password'}
              </button>
            </form>
          </>
        )}
      </div>

      <p style={{ marginTop: 24, fontSize: 12, color: '#9CA3AF', position: 'relative', zIndex: 1 }}>
        © 2026 OneBotAds. All rights reserved.
      </p>
    </div>
  );
};

export default ResetPassword;
